import React, { Component, Suspense } from 'react';
import packageJson from '../package.json';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';
import { apex, initApex, setGateway } from './apex';
import { connectionOpened, connectionClosed } from '@t7x000/apex-web/lib/redux/actions/wsConnectionActions';
import { getQueryParam } from './helpers/queryParamsHelper';
import { onInit } from '@t7x000/apex-web/lib/redux/actions/lifeCycleActions';
import StandAloneLayout from './layouts/StandAloneLayout/StandAloneLayout';
import './styles/theme/dark.css';
import './styles/RecentActivityPagination.theme.css';
import './styles/sidePanes/AdvancedOrderSidePane.theme.css';
import './styles/InstrumentSelector.theme.css';
// import SignupPage from './pages/SignupPage';
// import ProblemLoggingInPage from './pages/ProblemLoggingInPage';
import DepositConfirmation from '@t7x000/apex-web/lib/components/DepositConfirmation/DepositConfirmationModalContainer';
import Snackbar from '@t7x000/apex-web/lib/components/APSnackbar/APSnackbarContainer';
import TwoFactor from '@t7x000/apex-web/lib/components/TwoFactorAuthForm/TwoFactorAuthFormContainer';
import SidePane from '@t7x000/apex-web/lib/components/common/SidePane/SidePaneContainer';
import { emailLinkRoutes } from '@t7x000/apex-web/lib/helpers/emailLinksHelper';
import InteriorPage from './pages/InteriorPage';
// import LoginFormContainer from '@t7x000/apex-web/lib/components/Login/LoginFormContainer';
import { getGateway } from '@t7x000/apex-web/lib/helpers/wsHelper';
import redirectIfAuthorized from '@t7x000/apex-web/lib/hocs/redirectIfAuthorized';
// import { SimpleExchangePage } from './pages/Exchange/SimpleExchangePage';
import Require2FA from '@t7x000/apex-web/lib/components/2FA/TwoFactorAuthContainer';
import Enable2FA from '@t7x000/apex-web/lib/components/EnableTwoFactorAuth/StandaloneEnable2FAModalContainer';
import SendReceiveTwoFactorAuthContainer from '@t7x000/apex-web/lib/components/SendReceiveSidePane/SendReceiveTwoFactorAuth/SendReceiveTwoFactorAuthContainer';
import StandAloneKyc from '@t7x000/apex-web/lib/layouts/StandAloneKyc';
// DEV-8959: Travel Rule deposit modal
import TravelRuleDepositModal from '@t7x000/apex-web/lib/components/TravelRuleDepositModal';
import {
  marginBorrowerEnabledSelector,
  marginSystemEnabledSelector,
} from '@t7x000/apex-web/lib/redux/margin/selectors/marginSelectors';
import { ComponentsPage } from './demo/ComponentsPage';
import SilentLogin from './pages/SilentLogin';
import LoginTrade from './pages/LoginTrade';
import RedirectPage from './pages/RedirectPage';
import RedirectWithLoaderPage from './pages/RedirectWithLoaderPage';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { MaintenancePage } from './pages/MaintenancePage';
import ZendeskWidget from './components/ZendeskWidget/ZendeskWidget';
import { ZENDESK_ALLOWED_ROUTES } from './config/zendeskRoutes';
import { newRelicService, addGlobalFormSubmitEventListeners, destroyGlobalFormSubmitEventListeners } from './helpers';
import { UserRoles } from './constants';
import { hasLoginTradeBypass } from './helpers/loginTradeBypass';


class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMaintenanceMode: false,
      userRoles: [],
      isMaintenanceLoaded: false,
      areRolesLoaded: false,
      maintenanceModeError: false,
      userRolesError: false,
    };

    const gateway = getGateway();
    if (gateway) {
      setGateway(gateway);
      initApex(this.props.connectionOpened, this.props.connectionClosed);
    }

    const AffiliateTag = getQueryParam('aff');
    if (AffiliateTag) {
      window.localStorage.setItem('affiliateTag', AffiliateTag);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      window.scrollTo(0, 0);
    }

    if (this.props.userId !== prevProps.userId || this.props.sessionToken !== prevProps.sessionToken) {
      this.fetchUserRoles();
    }

    if (!newRelicService.isSubscriptionActive && apex.connection.ws) {
      newRelicService.subscribeToApexEvents(apex);
    }
  }

  componentDidMount() {
    addGlobalFormSubmitEventListeners();

    this.fetchMaintenanceMode();
    this.fetchUserRoles();
    // Fetch config file every minute to ensure we have the latest config
    this.intervalId = setInterval(this.fetchMaintenanceMode, 60 * 1000);
  }

  componentWillUnmount() {
    destroyGlobalFormSubmitEventListeners();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  fetchMaintenanceMode = async () => {
    this.setState({ isMaintenanceLoaded: false });
    try {
      const response = await fetch(process.env.REACT_APP_INTERNAL_CONFIG_URL, {
        cache: 'no-store',
      });
      const configData = await response.json();
      this.setState({
        isMaintenanceMode: configData.trading_web_maintenance_mode || false,
        maintenanceModeError: false,
      });
    } catch (error) {
      console.error('Failed to fetch maintenance mode:', error);
      this.setState({ isMaintenanceMode: false, maintenanceModeError: true });
    } finally {
      this.setState({ isMaintenanceLoaded: true });
    }
  };

  fetchUserRoles = async () => {
    // It's okay to wait for the login to complete to get userId and sessionToken.
    if (this.props.isAuthPending) {
      // We set areRolesLoaded to false, because isAuthPending = true means the user is trying to login now and
      // we need to wait for the login to complete to get userId and sessionToken.
      this.setState({ userRoles: [], areRolesLoaded: false, userRolesError: false });
      return;
    }

    // If login is complete but we don't have userId and sessionToken,
    // it means the user failed to login and doesn't have access to the app.
    if (!this.props.userId || !this.props.sessionToken) {
      // We set areRolesLoaded to true, because not having userId and sessionToken while
      // isAuthPending = false means the user is not trying to login.
      this.setState({ userRoles: [], areRolesLoaded: true, userRolesError: false });
      return;
    }

    this.setState({ areRolesLoaded: false, userRoles: [], userRolesError: false });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/v1/users/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.props.sessionToken}`,
          'x-user-id': this.props.userId,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        this.setState({ userRoles: [], userRolesError: true });
        return;
      }

      this.setState({ userRoles: Array.isArray(data.roles) ? data.roles : [], userRolesError: false });
    } catch (error) {
      console.error('Failed to fetch user role:', error);
      this.setState({ userRoles: [], userRolesError: true });
    } finally {
      this.setState({ areRolesLoaded: true });
    }
  };

  shouldShowMaintenancePage(pathname) {
    const { isMaintenanceMode } = this.state;
    const isFromLoginTrade =
      !!this.props.sessionToken && hasLoginTradeBypass(this.props.userId);
    const hasBypassMaintenanceRole = this.state.userRoles.some(
      (role) => role.RoleName === UserRoles.T7X_BypassMaintenance,
    );

    if (this.state.maintenanceModeError || this.state.userRolesError) {
      return false;
    }

    if (
      !isMaintenanceMode ||
      pathname === '/login-trade' ||
      isFromLoginTrade ||
      hasBypassMaintenanceRole
    ) {
      return false;
    }

    return true;
  }

  render() {
    const { pathname, search } = this.props.location;
    const { userId } = this.props;
    const onNomyxCallback = isVerifyWithToken(pathname, search);

    const isLoading =
      !onNomyxCallback &&
      (!this.state.isMaintenanceLoaded || !this.state.areRolesLoaded || this.props.isAuthPending);

    if (isLoading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '20px',
            fontWeight: 'bold',
          }}>
          Loading...
        </div>
      );
    }

    if (onNomyxCallback) {
      return (
        <Suspense fallback={null}>
          <SilentLogin />
        </Suspense>
      );
    }

    const shouldShowMaintenance = this.shouldShowMaintenancePage(pathname);

    if (shouldShowMaintenance) {
      return <MaintenancePage />;
    }

    // There's a second optional argument for setUserId which is needed only for complex
    // scenarios when we have multiple users per machine which is not the case here so
    // we don't need it.
    // More details: https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/setuserid/
    // eslint-disable-next-line no-undef
    const newRelic = newRelicService.getInstance();
    newRelic.setUserId(userId ? userId.toString() : null);

    return (
      <Suspense fallback={null}>
        <ZendeskWidget allowedRoutes={ZENDESK_ALLOWED_ROUTES} />
        <div className="App fluid container">
          <Switch>
            <Route
              path="/version"
              render={() => (
                <div>
                  <p>v3implementation-template: {packageJson.version}</p>
                  <p>apex-web: {packageJson.dependencies['@t7x000/apex-web']}</p>
                </div>
              )}
            />
            <Route exact path={'/'} component={RedirectWithLoaderPage} />
            {emailLinkRoutes(pathname, RedirectWithLoaderPage)}
            <Route path={'/login'} component={redirectIfAuthorized(RedirectPage)} />
            <Route path={'/standalonekyc'} component={StandAloneKyc} />
            <Route path={'/login-trade'} component={LoginTrade} />
            <Route path={'/signup'} component={RedirectPage} />
            <Route path={'/verify'} component={VerifyRoute} />
            {/* <Route path={'/problem-logging-in'} component={ProblemLoggingInPage} /> */}
            <Route
              path={'/twofactorauth'}
              render={() => (
                <StandAloneLayout>
                  <TwoFactor />
                </StandAloneLayout>
              )}
            />
            <Route path={'/enabletwofactorauth'} render={() => <Enable2FA />} />
            <Route path="/components" component={ComponentsPage} />
            <Route path="/terms" component={TermsAndConditions} />
            <Route path="/privacy" component={PrivacyPolicy} />
            {/* passing props.location prevents Blocked Re-renders: https://reacttraining.com/react-router/web/guides/dealing-with-update-blocking */}
            <InteriorPage
              location={this.props.location}
              systemMarginEnabled={this.props.systemMarginEnabled}
              marginBorrowerEnabled={this.props.marginBorrowerEnabled}
            />
          </Switch>
        </div>
        <Snackbar />
        <SidePane
          options={{
            ReportBlockTrade: {
              useNewForm: true,
            },
          }}
        />
        <DepositConfirmation />
        <Require2FA />
        <SendReceiveTwoFactorAuthContainer />
        <TravelRuleDepositModal />
      </Suspense>
    );
  }
}

const mapStateToProps = (state) => ({
  systemMarginEnabled: marginSystemEnabledSelector(state),
  marginBorrowerEnabled: marginBorrowerEnabledSelector(state),
  isAuthenticated: state.auth.isAuthenticated,
  userId: state.user.userInfo.UserId,
  sessionToken: state.auth.token,
  isAuthPending: state.auth.pending,
  authState: state.auth,
});

const mapDispatchToProps = {
  onInit,
  connectionOpened,
  connectionClosed,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));

const isVerifyWithToken = (pathname, search) => {
  return /\/verify$/.test(pathname) && !!new URLSearchParams(search).get('token');
}

const ProtectedSilentLogin = redirectIfAuthorized(SilentLogin, process.env.REACT_APP_DEFAULT_PATH);

const VerifyRoute = (props) => {
  const token = new URLSearchParams(props.location.search).get('token');
  // Always run SilentLogin while ?token= is present so Nomyx login proceeds even if a
  // login-trade session is still active. redirectIfAuthorized is bypassed intentionally.
  if (token) {
    return <SilentLogin {...props} />;
  }
  return <ProtectedSilentLogin {...props} />;
};